import React, { useState, useEffect } from 'react';
import { get, put } from '../../utils/apiClient';
import {
  AdminSectionTitle,
  AdminSectionText,
  AdminLoginLabel,
  AdminLoginInput,
  AdminLoginButton,
  AdminButton,
  AdminSaveButton,
} from '../../styles/AdminPageStyles';

const AdminDonatePage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await get('/donate');
        setPageData(data);
      } catch (err) {
        setError(err && err.message ? err.message : 'Failed to load donate page.');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  const handleSimpleFieldChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      [field]: value,
    }));
  };

  const handleBuildingAppealFieldChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      buildingProjectAppeal: {
        ...((prev && prev.buildingProjectAppeal) || {}),
        [field]: value,
      },
    }));
  };

  const updateCampaign = (index, field, value) => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const campaigns = Array.isArray(next.campaigns) ? [...next.campaigns] : [];
      const existing = campaigns[index] || {};
      campaigns[index] = {
        ...existing,
        [field]: value,
      };
      next.campaigns = campaigns;
      return next;
    });
  };

  const addCampaign = () => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const campaigns = Array.isArray(next.campaigns) ? [...next.campaigns] : [];
      campaigns.push({ title: '', description: '' });
      next.campaigns = campaigns;
      return next;
    });
  };

  const removeCampaign = (index) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev.campaigns)) return prev;
      const campaigns = prev.campaigns.filter((_, i) => i !== index);
      return {
        ...prev,
        campaigns,
      };
    });
  };

  const updateImpact = (index, field, value) => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const impacts = Array.isArray(next.impactMessages) ? [...next.impactMessages] : [];
      const existing = impacts[index] || {};
      impacts[index] = {
        ...existing,
        [field]: value,
      };
      next.impactMessages = impacts;
      return next;
    });
  };

  const addImpact = () => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const impacts = Array.isArray(next.impactMessages) ? [...next.impactMessages] : [];
      impacts.push({ title: '', description: '' });
      next.impactMessages = impacts;
      return next;
    });
  };

  const removeImpact = (index) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev.impactMessages)) return prev;
      const impacts = prev.impactMessages.filter((_, i) => i !== index);
      return {
        ...prev,
        impactMessages: impacts,
      };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!pageData) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const payload = {
        header: {
          ...(pageData.header || {}),
          title:
            (pageData.header && pageData.header.title) ||
            'Donate',
        },
        purposeText: pageData.purposeText || '',
        buildingProjectAppeal: pageData.buildingProjectAppeal || {},
        campaigns: Array.isArray(pageData.campaigns) ? pageData.campaigns : [],
        impactMessages: Array.isArray(pageData.impactMessages) ? pageData.impactMessages : [],
      };

      const updated = await put('/donate', payload);
      setPageData(updated);
      setSaveSuccess('Donate page saved successfully.');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to save donate page.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !pageData) {
    return (
      <>
        <AdminSectionTitle>Donate CMS</AdminSectionTitle>
        <AdminSectionText>Loading donate page...</AdminSectionText>
      </>
    );
  }

  return (
    <>
      <AdminSectionTitle>Donate CMS</AdminSectionTitle>

      <form onSubmit={handleSave}>
        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="purposeText">Purpose Text</AdminLoginLabel>
          <textarea
            id="purposeText"
            rows={3}
            value={(pageData && pageData.purposeText) || ''}
            onChange={(e) => handleSimpleFieldChange('purposeText', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="building-title">Building Project Title</AdminLoginLabel>
          <AdminLoginInput
            id="building-title"
            value={(pageData && pageData.buildingProjectAppeal && pageData.buildingProjectAppeal.title) || ''}
            onChange={(e) => handleBuildingAppealFieldChange('title', e.target.value)}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="building-text">Building Project Text</AdminLoginLabel>
          <textarea
            id="building-text"
            rows={3}
            value={(pageData && pageData.buildingProjectAppeal && pageData.buildingProjectAppeal.text) || ''}
            onChange={(e) => handleBuildingAppealFieldChange('text', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        <hr style={{ margin: '16px 0' }} />

        <AdminSectionTitle>Campaigns</AdminSectionTitle>
        <AdminSectionText style={{ marginBottom: 8 }}>
          Manage the list of donation campaigns.
        </AdminSectionText>

        {pageData && Array.isArray(pageData.campaigns) && pageData.campaigns.map((campaign, index) => (
          <div
            key={index}
            style={{
              border: '1px solid #ddd',
              borderRadius: 8,
              padding: 10,
              marginBottom: 8,
            }}
          >
            <div style={{ marginBottom: 8 }}>
              <AdminLoginLabel htmlFor={`campaign-title-${index}`}>
                Title
              </AdminLoginLabel>
              <AdminLoginInput
                id={`campaign-title-${index}`}
                value={campaign.title || ''}
                onChange={(e) => updateCampaign(index, 'title', e.target.value)}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <AdminLoginLabel htmlFor={`campaign-description-${index}`}>
                Description
              </AdminLoginLabel>
              <textarea
                id={`campaign-description-${index}`}
                rows={3}
                value={campaign.description || ''}
                onChange={(e) => updateCampaign(index, 'description', e.target.value)}
                style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
              />
            </div>
            <AdminButton type="button" onClick={() => removeCampaign(index)}>
              Remove Campaign
            </AdminButton>
          </div>
        ))}

        <div style={{ marginBottom: 16 }}>
          <AdminButton type="button" onClick={addCampaign}>
            Add Campaign
          </AdminButton>
        </div>

        <hr style={{ margin: '16px 0' }} />

        <AdminSectionTitle>Impact Messages</AdminSectionTitle>
        <AdminSectionText style={{ marginBottom: 8 }}>
          Manage the list of impact stories and messages.
        </AdminSectionText>

        {pageData && Array.isArray(pageData.impactMessages) && pageData.impactMessages.map((impact, index) => (
          <div
            key={index}
            style={{
              border: '1px solid #ddd',
              borderRadius: 8,
              padding: 10,
              marginBottom: 8,
            }}
          >
            <div style={{ marginBottom: 8 }}>
              <AdminLoginLabel htmlFor={`impact-title-${index}`}>
                Title
              </AdminLoginLabel>
              <AdminLoginInput
                id={`impact-title-${index}`}
                value={impact.title || ''}
                onChange={(e) => updateImpact(index, 'title', e.target.value)}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <AdminLoginLabel htmlFor={`impact-description-${index}`}>
                Description
              </AdminLoginLabel>
              <textarea
                id={`impact-description-${index}`}
                rows={3}
                value={impact.description || ''}
                onChange={(e) => updateImpact(index, 'description', e.target.value)}
                style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
              />
            </div>
            <AdminButton type="button" onClick={() => removeImpact(index)}>
              Remove Impact Message
            </AdminButton>
          </div>
        ))}

        <div style={{ marginBottom: 24 }}>
          <AdminButton type="button" onClick={addImpact}>
            Add Impact Message
          </AdminButton>
        </div>

        <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: '16px' }}>
          <AdminSaveButton type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Donate Page'}
          </AdminSaveButton>
          {saveSuccess && (
            <span style={{ color: '#1b5e20', fontWeight: '500' }}>{saveSuccess}</span>
          )}
          {error && (
            <span style={{ color: '#b71c1c', fontWeight: '500' }}>{error}</span>
          )}
        </div>
      </form>
    </>
  );
};

export default AdminDonatePage;
